<template>
  <v-dialog
    v-model="model"
    max-width="565"
    transition="dialog-bottom-transition"
    :persistent="busy"
    content-class="qr-dialog"
  >
    <v-card class="qr modal-card">
      <v-btn icon class="close-btn" @click.stop="close"><v-icon>mdi-close</v-icon></v-btn>

      <h2 class="modal-title">Выделить номера с<br/>маркировкой</h2>

      <div v-if="loading || firstOpen" class="loading">
        <v-progress-circular indeterminate size="24" class="mr-2"/><span>Загрузка…</span>
      </div>

      <div v-else-if="!markings.length" class="empty-tip">
        <div class="tip-title">У вас пока нет маркировок</div>

        <div class="memo">
          <div class="memo-title">
            <v-icon small class="mr-2">mdi-information-outline</v-icon>
            Памятка по маркировкам
          </div>
          <div class="memo-body">
            Маркировку создаёт менеджер. Пользователь самостоятельно
            маркировки не добавляет. Как только маркировка появится и станет «Активной», вы сможете
            оформить резерв по ней.
          </div>
        </div>

        <v-btn block class="primary-btn mt-4" @click="close">Понятно</v-btn>
      </div>

      <div v-else class="form">
        <section class="section">
          <div class="section-title">Название резерва</div>
          <v-text-field
            v-model.trim="form.title"
            dense
            outlined
            hide-details
            class="field"
            placeholder="Например: Резерв на сентябрь"
            :disabled="busy"
          />
        </section>

        <section class="section">
          <div class="section-title">Выберите маркировку</div>
          <v-select
            v-model="form.markingId"
            :items="markings"
            item-text="name"
            item-value="id"
            dense
            outlined
            hide-details
            class="field"
            :menu-props="{ offsetY:true, maxHeight: 320 }"
            item-color="#fee600"
            color="#4B5EFF"
            placeholder="Выберите из списка"
            :disabled="busy"
          />
        </section>

        <section class="section">
          <div class="section-title">Количество номеров</div>
          <v-text-field
            v-model.number="form.count"
            type="number"
            min="1"
            dense
            outlined
            hide-details
            class="field"
            placeholder="Например: 500"
            :disabled="busy"
          />
        </section>

        <section class="section price-section">
          <div class="price-card">
            <div class="price-col">
              <div class="price-label">Цена:</div>
              <div class="price-value">
                {{ ppn | rub }} <span class="muted">/ номер</span>
              </div>
            </div>
            <div class="price-col right">
              <div class="price-label">Со счёта будет списано:</div>
              <div class="price-value">{{ total | rub }}</div>
            </div>
          </div>
          <div v-if="apiError" class="money-error">{{ apiError }}</div>
        </section>

        <section class="section footnote">
          Номера станут доступны после подтверждения на стороне операторов связи, обычно в течение 2–3 дней.
          После подтверждения маркировки резерв номеров перейдёт в статус «Активен» и станет доступен
          для формирования каруселей.
        </section>

        <v-btn
          block
          height="48"
          class="primary-btn mt-2"
          :disabled="!isValid || busy"
          :loading="busy"
          @click="submit"
        >
          Выделить
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'QuickReserveModal',
  props: { value: { type: Boolean, required: true } },
  emits: ['input','created'],
  data(){
    return {
      busy:false,
      loading:false,
      firstOpen:true,
      apiError:'',
      markings: [],
      ppn: 0,
      form: { markingId: null, count: 1, title: '',autorenew:true }
    };
  },
  computed:{
    model:{ get(){ return this.value }, set(v){ this.$emit('input', v) } },
    total(){
      const price = Math.max(0, Number(this.ppn||0));
      const cnt   = Math.max(1, Number(this.form.count||0));
      return price * cnt;
    },
    isValid(){
      return !!this.form.markingId
        && Number(this.form.count) >= 1
        && (this.form.title || '').trim().length > 0;
    }
  },
  watch:{
    value: {
      immediate:true,
      async handler(v){
        if (!v) return;
        this.apiError = '';
        this.firstOpen = true;
        this.loading = true;
        this.form.autorenew = true;

        try {
          const m = await new Promise(resolve =>
            this.$root.$http.post('/numbers/markings/list', {}, r => resolve(r))
          );
          const arr = Array.isArray(m?.markings) ? m.markings : [];
          this.markings = arr.map(x => ({ id:String(x.id), name:x.name }));

          const p = await new Promise(resolve =>
            this.$root.$http.post('/numbers/price_per_number', {}, r => resolve(r))
          ).catch(()=>null);
          const price = Number(p?.price || 0);
          this.ppn = Number.isFinite(price) && price >= 0 ? price : 0;

          this.form.markingId = null;
          this.form.count = 1;
          this.form.title = '';
          this.form.autorenew = true;
        } finally {
          this.loading = false;
          this.firstOpen = false;
        }
      }
    }
  },
  methods:{
    close(){ if (!this.busy) this.model = false; },
    async submit(){
      if (!this.isValid) return;
      this.busy = true;
      this.apiError = '';
      try{
        const body = {
          markingId: this.form.markingId,
          count: Math.max(1, Number(this.form.count)||1),
          title: (this.form.title||'').trim(),
          price: Number(this.ppn)||0,
          mode: 'quick',
          autorenew: true
        };
        const resp = await new Promise(resolve =>
          this.$root.$http.post('/numbers/reserves/create_quick', body, r => resolve(r))
        );
        if (resp === 'error' || !resp?.ok) {
          this.apiError = (resp && resp.message) || 'Не удалось создать заявку';
          return;
        }
        this.$emit('created', { ...body, id: resp.id, total: this.total });
        this.model = false;
      } catch {
        this.apiError = 'Сбой сети/сервера';
      } finally {
        this.busy = false;
      }
    }
  },
  filters:{
    rub(v){
      const n = Number(v)||0;
      try { return new Intl.NumberFormat('ru-RU').format(n) + ' ₽'; }
      catch { return n + ' ₽'; }
    }
  }
};
</script>

<style scoped>
.qr{
  --c-text:#2B2D33;
  --c-muted:#6F7075;
  --c-line:#D9DADE;
  --c-bg:#F8F8F8;
  --c-accent:#4B5EFF;   /* фирменный синий */
  --c-ghost:#EEF0FF;
}
.qr-dialog{ align-items:flex-start; }
.modal-card{ position:relative; padding:28px 28px 24px; border-radius:8px; box-shadow:0 18px 50px rgba(0,0,0,.14); }
.close-btn{ position:absolute; top:12px; right:12px; min-width:36px; height:36px; }
.modal-title{ margin:4px 0 12px; text-align:center; font:800 24px/1.2 Manrope,system-ui,sans-serif; color:var(--c-text); }
.form{ margin-top:8px; }
.section{ margin-top:16px; }
.section-title{ font:700 15px/1.25 Manrope,sans-serif; color:var(--c-text); margin-bottom:6px; }

/* поля */
.field .v-input__slot{ min-height:40px; border-radius:8px; border-color:var(--c-line)!important; }
.field .v-input__slot:hover,
.field .v-input--is-focused .v-input__slot{ border-color:var(--c-accent)!important; }

/* цены */
.price-section{ margin-top:10px; }
.price-card{
  display:grid; grid-template-columns:1fr 1fr; gap:12px;
  background:#fff; border:1px solid var(--c-line); border-radius:8px; padding:12px 14px;
}
.price-col.right{ text-align:right; }
.price-label{ font:600 12px/1.2 Manrope,sans-serif; color:#7B7D83; margin-bottom:2px; }
.price-value{ font:800 20px/1.25 Manrope,sans-serif; color:var(--c-text); }
.price-value .muted{ font:800 14px/1.25 Manrope,sans-serif; opacity:.7; }

.money-error{ margin-top:10px; font:600 14px/1.25 Manrope,sans-serif; color:#EE505A; }

/* приписка */
.footnote{
  margin-top:12px; color:#6B6E75; background:var(--c-bg);
  border:1px solid var(--c-line); border-radius:8px; padding:10px 12px;
  font:500 13px/1.45 Manrope,sans-serif;
}

/* CTA как в приложении: синий фон, белый текст */
.primary-btn{
  background:#4B5EFF!important;
  color:#fff!important;
  font:800 16px/1.1 Manrope,sans-serif;
  border-radius:8px;
  text-transform:none; letter-spacing:0;
}

/* вспомогательное */
.loading{ display:flex; align-items:center; justify-content:center; height:160px; }
.empty-tip{ text-align:center; padding:8px 6px 2px; }
.tip-title{ font:800 18px/1.25 Manrope,sans-serif; color:var(--c-text); margin-bottom:6px; }
.memo{ margin-top:6px; background:#FFF9E9; border:1px solid #FFE4A6; border-radius:8px; padding:10px 12px; text-align:left; }
.memo-title{ font:700 13px/1.2 Manrope,sans-serif; color:#7A5B12; display:flex; align-items:center; }
.memo-body{ margin-top:6px; font:500 13px/1.45 Manrope,sans-serif; color:#7A5B12; }

@media (max-width: 500px){
  .price-card{ grid-template-columns:1fr; }
  .price-col.right{ text-align:left; }
}
</style>
